/* eslint-disable react/no-unescaped-entities */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLenis } from "lenis/react";
import { ArrowLeft } from "lucide-react";

const Para = ({ children }) => (
  <p className="mt-4 text-[15px] leading-7 text-[#A1A1AA]">{children}</p>
);

const Legal = ({ children }) => (
  <p className="mt-4 text-[13px] leading-6 tracking-wide text-[#8A8A8A]">{children}</p>
);

const Sub = ({ children }) => (
  <h3 className="mt-9 mb-1 font-mono text-xs uppercase tracking-[0.18em] text-white">
    {children}
  </h3>
);

const Bullets = ({ items }) => (
  <ul className="my-4 space-y-2.5">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3 text-[15px] leading-7 text-[#A1A1AA]">
        <span className="mt-[11px] h-1 w-1 shrink-0 bg-[#C9A94B]" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const Mail = () => (
  <a
    href="mailto:hello@prooftous.app"
    className="text-[#C9A94B] underline underline-offset-2 transition-colors hover:text-[#DCC271]"
  >
    hello@prooftous.app
  </a>
);

const Section = ({ n, title, children }) => (
  <section id={`sec-${n}`} className="mt-12 scroll-mt-28 border-t border-[#1F1F1F] pt-10">
    <div className="mb-4 flex items-baseline gap-4">
      <span className="font-mono text-sm text-[#C9A94B]">{String(n).padStart(2, "0")}</span>
      <h2 className="font-display text-3xl uppercase leading-none tracking-tight text-white md:text-4xl">
        {title}
      </h2>
    </div>
    {children}
  </section>
);

const TOC_ITEMS = [
  "Our services",
  "Intellectual property rights",
  "User representations",
  "User registration",
  "Subscriptions",
  "Prohibited activities",
  "User generated contributions",
  "Contribution license",
  "Mobile application license",
  "Services management",
  "Privacy policy",
  "Copyright infringements",
  "Term and termination",
  "Modifications and interruptions",
  "Governing law",
  "Dispute resolution",
  "Corrections",
  "Disclaimer",
  "Limitations of liability",
  "Indemnification",
  "User data",
  "Electronic communications, transactions, and signatures",
  "California users and residents",
  "Miscellaneous",
  "Contact us",
];

const PROHIBITED = [
  "Systematically retrieve data or other content from the Services to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us.",
  "Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords.",
  "Circumvent, disable, or otherwise interfere with security-related features of the Services.",
  "Disparage, tarnish, or otherwise harm, in our opinion, us and/or the Services.",
  "Use any information obtained from the Services in order to harass, abuse, or harm another person.",
  "Make improper use of our support services or submit false reports of abuse or misconduct.",
  "Use the Services in a manner inconsistent with any applicable laws or regulations.",
  "Engage in unauthorized framing of or linking to the Services.",
  "Upload or transmit viruses, Trojan horses, or other material (including excessive capital letters and spamming) that interferes with any party's use of the Services or modifies, impairs, or disrupts its operation.",
  "Engage in any automated use of the system, such as using scripts, data mining, robots, or similar data gathering and extraction tools.",
  "Delete the copyright or other proprietary rights notice from any Content.",
  "Attempt to impersonate another user or person or use the username of another user.",
  "Upload or transmit any material that acts as a passive or active information collection or transmission mechanism (e.g., gifs, 1×1 pixels, web bugs, cookies, or other 'spyware' or 'pcms').",
  "Interfere with, disrupt, or create an undue burden on the Services or the networks or services connected to the Services.",
  "Harass, annoy, intimidate, or threaten any of our employees or agents engaged in providing any portion of the Services to you.",
  "Attempt to bypass any measures of the Services designed to prevent or restrict access to the Services.",
  "Copy or adapt the Services' software, including but not limited to Flash, PHP, HTML, JavaScript, or other code.",
  "Except as permitted by applicable law, decipher, decompile, disassemble, or reverse engineer any of the software comprising the Services.",
  "Except as a result of standard search engine or browser usage, use, launch, or distribute any automated system (spider, robot, cheat utility, scraper, or offline reader) that accesses the Services.",
  "Use a buying agent or purchasing agent to make purchases on the Services.",
  "Make any unauthorized use of the Services, including collecting usernames and/or email addresses to send unsolicited email, or creating user accounts by automated means or under false pretenses.",
  "Use the Services as part of any effort to compete with us or for any revenue-generating endeavor or commercial enterprise.",
];

const CONTRIB_WARRANTIES = [
  "The creation, distribution, transmission, public display or performance, and the accessing, downloading, or copying of your Contributions do not and will not infringe the proprietary rights of any third party.",
  "You are the creator and owner of, or have the necessary licenses, rights, consents, releases, and permissions to use, your Contributions.",
  "You have the written consent, release, and/or permission of each identifiable individual person in your Contributions to use their name or likeness.",
  "Your Contributions are not false, inaccurate, or misleading.",
  "Your Contributions are not unsolicited or unauthorized advertising, promotional materials, pyramid schemes, chain letters, spam, mass mailings, or other forms of solicitation.",
  "Your Contributions are not obscene, lewd, lascivious, filthy, violent, harassing, libelous, slanderous, or otherwise objectionable.",
  "Your Contributions do not ridicule, mock, disparage, intimidate, or abuse anyone.",
  "Your Contributions are not used to harass or threaten any other person or to promote violence against a specific person or class of people.",
  "Your Contributions do not violate any applicable law, regulation, or rule.",
  "Your Contributions do not violate the privacy or publicity rights of any third party.",
  "Your Contributions do not violate any applicable law concerning child pornography, or otherwise intended to protect the health or well-being of minors.",
  "Your Contributions do not include any offensive comments connected to race, national origin, gender, sexual preference, or physical handicap.",
  "Your Contributions do not otherwise violate, or link to material that violates, any provision of these Legal Terms, or any applicable law or regulation.",
];

export default function Terms() {
  const lenis = useLenis();

  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [lenis]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el && lenis) lenis.scrollTo(el, { offset: -90 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="terms-page" className="grain min-h-screen bg-black text-white">
      <header className="sticky top-0 z-50 border-b border-[#1F1F1F] bg-black/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 md:px-8">
          <Link
            to="/"
            data-testid="terms-back-home"
            className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#8A8A8A] transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={2} />
            Back to home
          </Link>
          <span className="text-xl font-bold lowercase tracking-tight text-white">proof</span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 pb-28 pt-14 md:px-8">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C9A94B]">
          Legal · prooftous.app
        </span>
        <h1 className="mt-4 font-display text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
          Terms of Use
        </h1>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.15em] text-[#8A8A8A]">
          Last updated August 01, 2026
        </p>

        {/* Agreement */}
        <section className="mt-10">
          <h2 className="font-display text-2xl uppercase leading-none tracking-tight text-white md:text-3xl">
            Agreement to Our Legal Terms
          </h2>
          <Para>We are Proof It ("Company," "we," "us," "our").</Para>
          <Para>
            We operate the mobile application Proof It (the "App"), as well as any other related
            products and services that refer or link to these legal terms (the "Legal Terms")
            (collectively, the "Services"). You can contact us by email at <Mail />.
          </Para>
          <Para>
            These Legal Terms constitute a legally binding agreement made between you, whether
            personally or on behalf of an entity ("you"), and Proof It, concerning your access to and
            use of the Services. You agree that by accessing the Services, you have read, understood,
            and agreed to be bound by all of these Legal Terms. IF YOU DO NOT AGREE WITH ALL OF THESE
            LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SERVICES AND YOU MUST
            DISCONTINUE USE IMMEDIATELY.
          </Para>
          <Para>
            We will provide you with prior notice of any scheduled changes to the Services you are
            using. The modified Legal Terms will become effective upon posting or upon our notifying
            you by email. By continuing to use the Services after the effective date of any changes,
            you agree to be bound by the modified terms.
          </Para>
          <Para>
            The Services are intended for users who are at least 18 years old. Persons under the age of
            18 are not permitted to use or register for the Services. We recommend that you print a
            copy of these Legal Terms for your records.
          </Para>
        </section>

        {/* TOC */}
        <section className="mt-12 border-t border-[#1F1F1F] pt-10">
          <h2 className="font-display text-3xl uppercase leading-none tracking-tight text-white md:text-4xl">
            Table of Contents
          </h2>
          <ol className="mt-6 grid gap-2 sm:grid-cols-2">
            {TOC_ITEMS.map((t, i) => (
              <li key={i}>
                <button
                  onClick={() => go(`sec-${i + 1}`)}
                  className="group flex w-full items-baseline gap-3 text-left"
                >
                  <span className="font-mono text-xs text-[#C9A94B]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[15px] text-[#A1A1AA] underline-offset-4 group-hover:text-white group-hover:underline">
                    {t}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </section>

        <Section n={1} title="Our Services">
          <Para>
            The information provided when using the Services is not intended for distribution to or use
            by any person or entity in any jurisdiction or country where such distribution or use would
            be contrary to law or regulation or which would subject us to any registration requirement
            within such jurisdiction or country. Those who access the Services from other locations do
            so on their own initiative and are solely responsible for compliance with local laws.
          </Para>
          <Para>
            The Services are not tailored to comply with industry-specific regulations (HIPAA, FISMA,
            etc.), so if your interactions would be subjected to such laws, you may not use the
            Services. You may not use the Services in a way that would violate the Gramm-Leach-Bliley
            Act (GLBA).
          </Para>
        </Section>

        <Section n={2} title="Intellectual Property Rights">
          <Sub>Our intellectual property</Sub>
          <Para>
            We are the owner or the licensee of all intellectual property rights in our Services,
            including all source code, databases, functionality, software, website designs, audio,
            video, text, photographs, and graphics in the Services (the "Content"), as well as the
            trademarks, service marks, and logos contained therein (the "Marks"). The Content and Marks
            are provided "AS IS" for your personal, non-commercial use or internal business purpose
            only.
          </Para>
          <Sub>Your use of our Services</Sub>
          <Para>
            Subject to your compliance with these Legal Terms, we grant you a non-exclusive,
            non-transferable, revocable license to access the Services and to download or print a copy
            of any portion of the Content to which you have properly gained access, solely for your
            personal, non-commercial use or internal business purpose. No part of the Services and no
            Content or Marks may be copied, reproduced, republished, uploaded, posted, publicly
            displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise
            exploited for any commercial purpose without our express prior written permission. Requests
            may be sent to <Mail />.
          </Para>
          <Sub>Your submissions and contributions</Sub>
          <Para>
            <span className="text-white">Submissions:</span> By directly sending us any question,
            comment, suggestion, idea, feedback, or other information about the Services
            ("Submissions"), you agree to assign to us all intellectual property rights in such
            Submission. We shall own it and be entitled to its unrestricted use for any lawful purpose,
            without acknowledgment or compensation to you.
          </Para>
          <Para>
            <span className="text-white">Contributions:</span> The Services may invite you to
            contribute content ("Contributions") which may be viewable by other users. By posting
            Contributions, you grant us an unrestricted, irrevocable, perpetual, non-exclusive,
            transferable, royalty-free, worldwide license to use, copy, reproduce, distribute, sell,
            publish, broadcast, store, publicly display, reformat, translate, and exploit your
            Contributions (including your image, name, and voice) for any purpose. You are solely
            responsible for what you post, and you confirm your Contributions are original to you, are
            not confidential, and do not violate our Prohibited Activities or any law. We may remove or
            edit your Content at any time without notice.
          </Para>
        </Section>

        <Section n={3} title="User Representations">
          <Para>
            By using the Services, you represent and warrant that: (1) all registration information you
            submit will be true, accurate, current, and complete; (2) you will maintain the accuracy of
            such information; (3) you have the legal capacity and you agree to comply with these Legal
            Terms; (4) you are not a minor in the jurisdiction in which you reside; (5) you will not
            access the Services through automated or non-human means; (6) you will not use the Services
            for any illegal or unauthorized purpose; and (7) your use of the Services will not violate
            any applicable law or regulation. If you provide any information that is untrue or
            incomplete, we may suspend or terminate your account.
          </Para>
        </Section>

        <Section n={4} title="User Registration">
          <Para>
            You may be required to register to use the Services. You agree to keep your password
            confidential and will be responsible for all use of your account and password. We reserve
            the right to remove, reclaim, or change a username you select if we determine, in our sole
            discretion, that such username is inappropriate, obscene, or otherwise objectionable.
          </Para>
        </Section>

        <Section n={5} title="Subscriptions">
          <Para>
            Proof It offers an optional paid subscription ("Proof Pro"). All purchases, billing, and
            payment processing for subscriptions are handled by the Apple App Store through your Apple
            Account. We use RevenueCat as a subscription-management intermediary to validate purchases
            and manage your subscription status and entitlements. We do not receive or store your
            payment card details.
          </Para>
          <Sub>Billing and Renewal</Sub>
          <Para>
            Your subscription is billed through your Apple App Store account and will automatically
            renew unless auto-renew is turned off at least 24 hours before the end of the current
            period. Your Apple Account will be charged within 24 hours prior to the end of the current
            period. The length of your billing cycle is monthly.
          </Para>
          <Sub>Free Trial</Sub>
          <Para>
            We offer a 30-day free trial to new users. If you do not cancel before the end of the free
            trial, your subscription will automatically convert to a paid subscription and your Apple
            Account will be charged according to your selected plan.
          </Para>
          <Sub>Cancellation</Sub>
          <Para>
            You can manage or cancel your subscription at any time in your Apple App Store account
            settings (Settings → your name → Subscriptions). Cancellation takes effect at the end of the
            current paid term. Deleting the App does not automatically cancel your subscription. Any
            questions? Email <Mail />.
          </Para>
          <Sub>Fee Changes</Sub>
          <Para>
            We may make changes to the subscription fee and will communicate any price changes to you in
            accordance with applicable law and the Apple App Store's requirements.
          </Para>
          <Sub>Refunds</Sub>
          <Para>
            Because purchases are processed by Apple, all refund requests are handled by Apple in
            accordance with the App Store's policies. We do not directly process refunds for App Store
            purchases.
          </Para>
        </Section>

        <Section n={6} title="Prohibited Activities">
          <Para>
            You may not access or use the Services for any purpose other than that for which we make the
            Services available. As a user of the Services, you agree not to:
          </Para>
          <Bullets items={PROHIBITED} />
        </Section>

        <Section n={7} title="User Generated Contributions">
          <Para>
            The Services may provide you the opportunity to create, submit, post, or broadcast content
            ("Contributions"). Contributions may be viewable by other users and through third-party
            websites, and may be treated as non-confidential and non-proprietary. When you make
            available any Contributions, you represent and warrant that:
          </Para>
          <Bullets items={CONTRIB_WARRANTIES} />
          <Para>
            Any use of the Services in violation of the foregoing violates these Legal Terms and may
            result in termination or suspension of your rights to use the Services.
          </Para>
        </Section>

        <Section n={8} title="Contribution License">
          <Para>
            By posting your Contributions to any part of the Services, you automatically grant us an
            unrestricted, unlimited, irrevocable, perpetual, non-exclusive, transferable, royalty-free,
            fully-paid, worldwide right and license to host, use, copy, reproduce, disclose, sell,
            publish, broadcast, store, cache, publicly perform, publicly display, reformat, translate,
            transmit, excerpt, and distribute such Contributions (including your image and voice) for
            any purpose. We do not assert any ownership over your Contributions — you retain full
            ownership of all of your Contributions and any intellectual property rights associated with
            them. We have the right, in our sole discretion, to edit, re-categorize, pre-screen, or
            delete any Contributions at any time and for any reason, without notice.
          </Para>
        </Section>

        <Section n={9} title="Mobile Application License">
          <Sub>Use License</Sub>
          <Para>
            If you access the Services via the App, we grant you a revocable, non-exclusive,
            non-transferable, limited right to install and use the App on wireless electronic devices
            owned or controlled by you, strictly in accordance with these Legal Terms. You shall not:
            (1) decompile, reverse engineer, disassemble, or decrypt the App; (2) make any modification
            or derivative work from the App; (3) violate any applicable laws in connection with your use
            of the App; (4) remove or obscure any proprietary notice; (5) use the App for any
            revenue-generating endeavor for which it is not designed; (6) make the App available over a
            network permitting access by multiple devices at the same time; (7) use the App to create a
            competitive product; (8) use the App to send automated queries or unsolicited commercial
            email; or (9) use our proprietary information in the design of any applications, accessories,
            or devices.
          </Para>
          <Sub>Apple and Android Devices</Sub>
          <Para>
            When you use the App obtained from the Apple Store or Google Play (each an "App
            Distributor"): the license granted is limited to a non-transferable license to use the App
            on a device that utilizes the Apple iOS or Android operating systems in accordance with the
            App Distributor's usage rules; we (not the App Distributor) are responsible for providing
            maintenance and support; in the event of any failure of the App to conform to any warranty,
            the App Distributor may refund the purchase price, if any; you represent that you are not
            located in an embargoed country or on any US government restricted-parties list; you must
            comply with applicable third-party terms; and you acknowledge that the App Distributors are
            third-party beneficiaries entitled to enforce these terms against you.
          </Para>
        </Section>

        <Section n={10} title="Services Management">
          <Para>
            We reserve the right, but not the obligation, to: (1) monitor the Services for violations of
            these Legal Terms; (2) take appropriate legal action against anyone who violates the law or
            these Legal Terms; (3) refuse, restrict access to, limit the availability of, or disable any
            of your Contributions; (4) remove or disable all files and content that are excessive in
            size or burdensome to our systems; and (5) otherwise manage the Services to protect our
            rights and property and facilitate the proper functioning of the Services.
          </Para>
        </Section>

        <Section n={11} title="Privacy Policy">
          <Para>
            We care about data privacy and security. Please review our{" "}
            <Link
              to="/privacy"
              className="text-[#C9A94B] underline underline-offset-2 transition-colors hover:text-[#DCC271]"
            >
              Privacy Policy
            </Link>
            . By using the Services, you agree to be bound by our Privacy Policy, which is incorporated
            into these Legal Terms. The Services are hosted in the United States. If you access the
            Services from another region with laws governing personal data that differ from US law,
            then through your continued use of the Services you are transferring your data to the United
            States and expressly consent to have your data transferred to and processed in the United
            States.
          </Para>
        </Section>

        <Section n={12} title="Copyright Infringements">
          <Para>
            We respect the intellectual property rights of others. If you believe that any material
            available on or through the Services infringes upon any copyright you own or control, please
            immediately notify us using the contact information provided below (a "Notification"). A
            copy of your Notification will be sent to the person who posted or stored the material.
            Please be advised that pursuant to applicable law you may be held liable for damages if you
            make material misrepresentations in a Notification.
          </Para>
        </Section>

        <Section n={13} title="Term and Termination">
          <Legal>
            THESE LEGAL TERMS SHALL REMAIN IN FULL FORCE AND EFFECT WHILE YOU USE THE SERVICES. WE
            RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO
            AND USE OF THE SERVICES TO ANY PERSON FOR ANY REASON, INCLUDING FOR BREACH OF ANY
            REPRESENTATION, WARRANTY, OR COVENANT CONTAINED IN THESE LEGAL TERMS OR OF ANY APPLICABLE
            LAW. WE MAY TERMINATE YOUR USE OR DELETE YOUR ACCOUNT AND ANY CONTENT AT ANY TIME, WITHOUT
            WARNING, IN OUR SOLE DISCRETION.
          </Legal>
          <Para>
            If we terminate or suspend your account for any reason, you are prohibited from registering
            and creating a new account under your name, a fake or borrowed name, or the name of any
            third party. We also reserve the right to take appropriate legal action, including pursuing
            civil, criminal, and injunctive redress.
          </Para>
        </Section>

        <Section n={14} title="Modifications and Interruptions">
          <Para>
            We reserve the right to change, modify, or remove the contents of the Services at any time or
            for any reason at our sole discretion without notice. We will not be liable to you or any
            third party for any modification, price change, suspension, or discontinuance of the
            Services. We cannot guarantee the Services will be available at all times and are not liable
            for any loss, damage, or inconvenience caused by your inability to access the Services during
            any downtime.
          </Para>
        </Section>

        <Section n={15} title="Governing Law">
          <Para>
            These Legal Terms and your use of the Services are governed by and construed in accordance
            with the laws of Germany applicable to agreements made and to be entirely performed within
            Germany, without regard to its conflict of law principles. This choice of law does not
            deprive consumers of the protection afforded to them by provisions that cannot be derogated
            from by agreement under the law of their country of habitual residence.
          </Para>
        </Section>

        <Section n={16} title="Dispute Resolution">
          <Sub>Informal Negotiations</Sub>
          <Para>
            To expedite resolution and control cost, the Parties agree to first attempt to negotiate any
            Dispute (except those expressly excepted below) informally for at least sixty (60) days
            before initiating arbitration. Such informal negotiations commence upon written notice from
            one Party to the other.
          </Para>
          <Sub>Binding Arbitration</Sub>
          <Para>
            Any dispute shall be determined by one arbitrator chosen in accordance with the Arbitration
            and Internal Rules of the European Court of Arbitration (European Centre of Arbitration,
            Strasbourg). The seat of arbitration shall be Berlin, Germany. The language of the
            proceedings shall be English. Applicable substantive law shall be the law of Germany.
          </Para>
          <Sub>Restrictions</Sub>
          <Para>
            Any arbitration shall be limited to the Dispute between the Parties individually. No
            arbitration shall be joined with any other proceeding; there is no right to arbitrate on a
            class-action basis; and there is no right to bring a Dispute in a purported representative
            capacity.
          </Para>
          <Sub>Exceptions</Sub>
          <Para>
            The following Disputes are not subject to informal negotiations and binding arbitration: (a)
            Disputes concerning the intellectual property rights of a Party; (b) Disputes related to
            allegations of theft, piracy, invasion of privacy, or unauthorized use; and (c) any claim for
            injunctive relief.
          </Para>
        </Section>

        <Section n={17} title="Corrections">
          <Para>
            There may be information on the Services that contains typographical errors, inaccuracies, or
            omissions, including descriptions, pricing, availability, and various other information. We
            reserve the right to correct any errors and to change or update the information on the
            Services at any time, without prior notice.
          </Para>
        </Section>

        <Section n={18} title="Disclaimer">
          <Legal>
            THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE
            SERVICES WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
            WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SERVICES AND YOUR USE THEREOF,
            INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO WARRANTIES OR REPRESENTATIONS ABOUT THE
            ACCURACY OR COMPLETENESS OF THE SERVICES' CONTENT AND WE WILL ASSUME NO LIABILITY FOR ANY (1)
            ERRORS OR INACCURACIES OF CONTENT, (2) PERSONAL INJURY OR PROPERTY DAMAGE RESULTING FROM YOUR
            USE OF THE SERVICES, (3) ANY UNAUTHORIZED ACCESS TO OUR SECURE SERVERS, (4) ANY INTERRUPTION
            OF TRANSMISSION TO OR FROM THE SERVICES, (5) ANY BUGS, VIRUSES, OR THE LIKE TRANSMITTED
            THROUGH THE SERVICES BY ANY THIRD PARTY, AND/OR (6) ANY ERRORS OR OMISSIONS IN ANY CONTENT.
            YOU SHOULD USE YOUR BEST JUDGMENT AND EXERCISE CAUTION WHERE APPROPRIATE.
          </Legal>
        </Section>

        <Section n={19} title="Limitations of Liability">
          <Legal>
            IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY
            FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES,
            INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM YOUR USE OF
            THE SERVICES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR LIABILITY
            TO YOU FOR ANY CAUSE WHATSOEVER WILL AT ALL TIMES BE LIMITED TO THE LESSER OF THE AMOUNT PAID,
            IF ANY, BY YOU TO US DURING THE ONE (1) MONTH PERIOD PRIOR TO ANY CAUSE OF ACTION ARISING OR
            $25.00 USD. CERTAIN US STATE AND INTERNATIONAL LAWS MAY NOT ALLOW THESE LIMITATIONS, SO SOME
            OR ALL OF THE ABOVE MAY NOT APPLY TO YOU.
          </Legal>
        </Section>

        <Section n={20} title="Indemnification">
          <Para>
            You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates,
            and all of our respective officers, agents, partners, and employees, from and against any
            loss, damage, liability, claim, or demand, including reasonable attorneys' fees and expenses,
            made by any third party due to or arising out of: (1) your Contributions; (2) use of the
            Services; (3) breach of these Legal Terms; (4) any breach of your representations and
            warranties; (5) your violation of the rights of a third party; or (6) any overt harmful act
            toward any other user with whom you connected via the Services. We reserve the right, at your
            expense, to assume the exclusive defense and control of any matter for which you are required
            to indemnify us.
          </Para>
        </Section>

        <Section n={21} title="User Data">
          <Para>
            We will maintain certain data that you transmit to the Services for the purpose of managing
            the performance of the Services, as well as data relating to your use of the Services.
            Although we perform regular routine backups of data, you are solely responsible for all data
            that you transmit or that relates to any activity you have undertaken using the Services. You
            agree that we shall have no liability to you for any loss or corruption of any such data.
          </Para>
        </Section>

        <Section n={22} title="Electronic Communications, Transactions, and Signatures">
          <Para>
            Visiting the Services, sending us emails, and completing online forms constitute electronic
            communications. You consent to receive electronic communications, and you agree that all
            agreements, notices, disclosures, and other communications we provide to you electronically
            satisfy any legal requirement that such communication be in writing. YOU HEREBY AGREE TO THE
            USE OF ELECTRONIC SIGNATURES, CONTRACTS, ORDERS, AND OTHER RECORDS, AND TO ELECTRONIC DELIVERY
            OF NOTICES, POLICIES, AND RECORDS OF TRANSACTIONS INITIATED OR COMPLETED BY US OR VIA THE
            SERVICES.
          </Para>
        </Section>

        <Section n={23} title="California Users and Residents">
          <Para>
            If any complaint with us is not satisfactorily resolved, you can contact the Complaint
            Assistance Unit of the Division of Consumer Services of the California Department of Consumer
            Affairs in writing at 1625 North Market Blvd., Suite N 112, Sacramento, California 95834 or by
            telephone at (800) 952-5210 or (916) 445-1254.
          </Para>
        </Section>

        <Section n={24} title="Miscellaneous">
          <Para>
            These Legal Terms and any policies or operating rules posted by us constitute the entire
            agreement and understanding between you and us. Our failure to exercise or enforce any right
            or provision shall not operate as a waiver. We may assign any or all of our rights and
            obligations to others at any time. We shall not be responsible for any loss, damage, delay,
            or failure to act caused by any cause beyond our reasonable control. If any provision is
            determined to be unlawful, void, or unenforceable, that provision is deemed severable and
            does not affect the validity of the remaining provisions. There is no joint venture,
            partnership, employment, or agency relationship created between you and us. You waive any
            defenses based on the electronic form of these Legal Terms.
          </Para>
        </Section>

        <Section n={25} title="Contact Us">
          <Para>
            In order to resolve a complaint regarding the Services or to receive further information
            regarding use of the Services, please contact us at:
          </Para>
          <div className="mt-5 border border-[#1F1F1F] bg-[#0A0A0A] p-5 font-mono text-sm">
            <div className="text-white">Proof It</div>
            <div className="mt-1 text-[#C9A94B]">hello@prooftous.app</div>
          </div>
        </Section>

        <div className="mt-16 border-t border-[#1F1F1F] pt-8">
          <Link
            to="/"
            data-testid="terms-back-home-bottom"
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#C9A94B] transition-colors hover:text-[#DCC271]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={2} />
            Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}
